import './Welcome.scss'

import { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { Button, Col, Container, Form, Row } from 'react-bootstrap'

import cn from 'classnames'

import { navigateTo } from 'components/common/navigation'
import { ROUTE_ASSESSMENT_PROGRAM } from 'components/common/helpers/Paths'
import {
  postMobileToken,
  postMobileKeychain,
  toggleTabBar,
  isInApp
} from 'components/common/helpers/Mobile'
import {
  clearVerificationData,
  getAuthenticatedUserToken,
  fetchVerificationData,
  setLocalAuthKey,
  getAuthenticatedUser
} from 'components/auth'
import {
  EVENT_ACTION_COMPLETE,
  EVENT_ACTION_PAGEVIEW,
  EVENT_ACTION_START,
  EVENT_CATEGORY_SUBSCRIPTION,
  EVENT_CATEGORY_WELCOME,
  saveUserEvent,
  trackGAEvent,
  trackMetaEvent
} from 'components/common/api/UserEvents'
import PaymentForm from 'components/payments/PaymentForm'
import { useContext } from 'react'
import SubscriptionContext from 'components/common/contexts/SubscriptionContext'
import { getCurrentSubscription } from 'components/common/api/Payments'
import Onboarding from './Onboarding'

/**
 * Simple Welcome component. Used to display the welcome screen and onboarding
 */
export default function Welcome() {
  const history = useHistory()
  const { setUserSubscription } = useContext(SubscriptionContext)

  const [onboardingComplete, setOnboardingComplete] = useState(false)
  const [didProcessTransaction, setDidProcessTransaction] = useState(false)
  const [useFreeTrial, setUseFreeTrial] = useState(false)

  // Update the user's subscription context
  const updateSubscriptionAsync = async () => {
    const sub = await getCurrentSubscription()
    setUserSubscription(sub)
  }

  /**
   * Handle the user clicking the "Let's Remble" button, after they've completed onboarding
   */
  const handleOnboardingComplete = () => {
    // Now we can try to post to the mobile token bridge, but fail silently
    try {
      // Track to Meta
      trackMetaEvent('CompleteRegistration', {
        content_name: 'Completed Signup'
      })

      // Track to GA
      trackGAEvent('signup', 'signup', 'Completed Signup')

      // Log the event
      saveUserEvent(EVENT_CATEGORY_WELCOME, EVENT_ACTION_COMPLETE)

      const token = getAuthenticatedUserToken()
      const { email, password } = fetchVerificationData()

      // Post the token and credentials to the mobile token bridge
      if (token && email && password) {
        clearVerificationData()

        // Post the token and credentials to the mobile token bridge
        if (isInApp()) {
          // Get the user data from the auth token
          const authenticatedUser = getAuthenticatedUser(token)
          const remble_id = authenticatedUser.aurora_id

          postMobileKeychain({ email, password, remble_id }) // Store credentials in iOS/Android keychain
          postMobileToken(token) // Store token for app and dismiss AuthViewController in old app
          setLocalAuthKey(token) // Sets local storage to dismiss the in-app auth screen
        }
      }
    } catch (e) {
      // Do nothing. The user may have already verified their account
      // Or the mobile token bridge is not available
      console.error(e)
    }

    navigateTo(ROUTE_ASSESSMENT_PROGRAM, history)
  }

  /**
   * Handle payment form success
   * @param {Object} data The payment form data
   */
  const handlePaymentSuccess = data => {
    // Update our transaction states
    setDidProcessTransaction(true)

    // Update the user's subscription context
    updateSubscriptionAsync()

    // Track to Meta
    trackMetaEvent('Subscribe', {
      content_name: 'Purchased Subscription',
      currency: 'USD',
      value: data?.plan_cost
    })

    // Track to GA
    trackGAEvent(
      'subscribe',
      'subscribe',
      'Purchased Subscription',
      '' + data?.plan_cost,
      data?.plan_cost
    )

    // Log the event
    saveUserEvent(
      EVENT_CATEGORY_SUBSCRIPTION,
      EVENT_ACTION_START,
      data?.plan_name,
      data?.signup_code
    )
  }

  /**
   * Handle the user toggling the "Enable Trial" switch
   * @param {Object} event The event object
   */
  const handleTrialToggle = event => {
    // Return early if the event is undefined
    if (!event) return

    // Toggle the free trial state
    setUseFreeTrial(event.currentTarget.checked)
  }

  // On mount, hide the tab bar and log the page view
  useEffect(() => {
    // Hide tab bar on mobile
    toggleTabBar('off')

    // Log access to the page
    saveUserEvent(EVENT_CATEGORY_WELCOME, EVENT_ACTION_PAGEVIEW)
  }, [])

  return (
    <Container className='welcome-container'>
      <Row>
        <Col>
          {/* Onboarding */}
          {!onboardingComplete ? (
            <Onboarding handleComplete={_ => setOnboardingComplete(true)} />
          ) : (
            <div className='mt-3'>
              {/* Onboarding complete */}
              {useFreeTrial || didProcessTransaction ? (
                <>
                  <h1 className='title mb-0'>Welcome to Remble</h1>
                  <h4 className='subtitle green mb-4'>
                    {useFreeTrial
                      ? '7-day Trial. Unlimited Access.'
                      : 'Unlimited Access Unlocked'}
                  </h4>
                </>
              ) : (
                <>
                  <h1 className='title mb-0'>Select a Plan</h1>
                  <h4 className='subtitle'>Choose a plan that works for you</h4>
                </>
              )}

              {/* Don't show trial toggle if we processed a transaction */}
              {!didProcessTransaction && (
                <Form.Group
                  className={cn('form-group visible trial-switch mt-3', {
                    enabled: useFreeTrial
                  })}
                >
                  <Form.Check
                    id='enableTrial'
                    className='text-left'
                    size='lg'
                    label={
                      useFreeTrial
                        ? 'Start your 7 day free trial'
                        : 'Not ready yet? Start free 7 day trial'
                    }
                    type='switch'
                    onChange={handleTrialToggle}
                    checked={useFreeTrial}
                    reverse
                  />
                </Form.Group>
              )}

              {/* If the user has not opted into a free trial, show the payment form  */}
              {!useFreeTrial && !didProcessTransaction ? (
                <PaymentForm
                  isInline={true}
                  onPaymentSuccess={handlePaymentSuccess}
                  showTrialToggle={false}
                />
              ) : (
                <Button
                  onClick={handleOnboardingComplete}
                  size='lg'
                  variant='primary'
                  className='mt-5 px-5 w-100'
                >
                  Let's Remble
                </Button>
              )}
            </div>
          )}
        </Col>
      </Row>
    </Container>
  )
}
